import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import EinzugsdateiKnopf from "../components/EinzugsdateiKnopf";
import {
  useEinzugLaeufe, useEinzugVorschau, useEinzugsdateiErneut, useLaufStornieren, useMandate,
  useQontoLauf, useQontoStatus,
} from "../hooks/useLastschrift";
import type { MandatZeile } from "../hooks/useLastschrift";
import { fehlertext } from "../lib/fehler";
import { periodeDe } from "../lib/zahl";
import "../styles/lastschrift.css";

const tag = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");
const euro = (s: string | null | undefined) => (s ? `${Number(s).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €` : "—");

const WEG_TEXT = {
  aus: "Einzug abgeschaltet — Kunden zahlen per Überweisung",
  datei: "Einzug per Datei über die Sparkasse",
  qonto: "Einzug über Qonto",
} as const;

/**
 * Zahlungen — fällige Raten, Mandate und Einzüge der Pflegeverträge.
 * Bis 09.10.2026 hieß die Seite „Lastschrift" und trug oben den Umschalter für
 * den Einzugsweg; der steht jetzt unter Einstellungen, hier nur sein Stand.
 */
export default function LastschriftPage() {
  const { data: vorschau } = useEinzugVorschau();
  const weg = vorschau?.einzugsweg;
  return (
    <Shell title="Zahlungen" subtitle={weg ? WEG_TEXT[weg] : "Raten der Pflegeverträge"}
      action={<Link className="pf-btn is-ghost" to="/einstellungen">Einzugsweg ändern</Link>}>
      <Anstehend />
      <Mandate />
      <Laeufe />
    </Shell>
  );
}

function Anstehend() {
  const { data: v, isLoading } = useEinzugVorschau();
  const { data: status } = useQontoStatus();
  const lauf = useQontoLauf();
  const bereit = (v?.posten || []).filter(p => p.qonto_bereit);
  const rest = (v?.posten || []).filter(p => !p.qonto_bereit);

  return (
    <>
      <div className="pf-seclabel">Fällig{v ? ` bis ${periodeDe(v.bis_periode)}` : ""}</div>
      <div className="pf-card ls-karte">
        {isLoading ? <p className="ls-text">Lade…</p> : !v?.posten.length && !v?.hindernisse.length ? (
          <p className="ls-text">Zurzeit ist keine Rate fällig.</p>
        ) : (
          <>
            {bereit.length > 0 && (
              <ul className="ls-liste">
                {bereit.map(p => (
                  <li key={p.abrechnung_id}>
                    <span>{p.kunde_name} · {p.nummer} · {periodeDe(p.periode)}</span>
                    <b>{euro(p.betrag_brutto)}{p.qonto_erste_rate ? " · zieht Qonto mit dem Mandat" : ""}</b>
                  </li>
                ))}
              </ul>
            )}
            {rest.length > 0 && (
              <>
                <p className="ls-text">
                  {v?.einzugsweg === "datei" ? "Per Einzugsdatei:" : "Ohne Qonto-Mandat (nur über die Sparkassen-Datei möglich):"}
                </p>
                <ul className="ls-liste">
                  {rest.map(p => (
                    <li key={p.abrechnung_id}><span>{p.kunde_name} · {p.nummer} · {periodeDe(p.periode)}</span>
                      <b>{euro(p.betrag_brutto)}</b></li>
                  ))}
                </ul>
              </>
            )}
            {v && v.hindernisse.length > 0 && (
              <>
                <p className="ls-text">Hängt fest:</p>
                <ul className="ls-liste">
                  {v.hindernisse.map(h => (
                    <li key={`${h.vertrag_id}-${h.periode}`}>
                      <span>{h.kunde_name} · {h.nummer} · {periodeDe(h.periode)} · {euro(h.betrag_brutto)}</span>
                      <b className="ls-grund">{h.grund}</b>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {v?.einzugsweg === "datei" && <EinzugsdateiKnopf v={v} />}
            {v?.einzugsweg === "qonto" && status?.verbunden && bereit.length > 0 && (
              <button type="button" className="pf-btn ls-knopf" disabled={lauf.isPending}
                onClick={() => lauf.mutate()}>
                {lauf.isPending ? "Reicht ein…" : `Jetzt einreichen (${bereit.length})`}
              </button>
            )}
            {lauf.data && (
              <p className="ls-text">
                Eingereicht: {lauf.data.eingereicht}, fällig {tag(lauf.data.faellig_am)}.
                {lauf.data.fehlgeschlagen.length > 0 && ` Nicht eingereicht: ${lauf.data.fehlgeschlagen.length}.`}
              </p>
            )}
            {lauf.error && <p className="ls-fehler">{fehlertext(lauf.error)}</p>}
          </>
        )}
      </div>
    </>
  );
}

function mandatText(m: MandatZeile, ueberQonto: boolean): { klasse: string; text: string } {
  if (m.weg === "keins") return { klasse: "ls-offen", text: "Noch kein Mandat" };
  if (m.weg === "eigen") return { klasse: "ls-gut", text: "Eigenes Mandat (Sparkasse)" };
  // Ein Qonto-Versuch zählt nur, solange Qonto der Einzugsweg ist.
  if (!ueberQonto && m.qonto_status !== "approved") return { klasse: "ls-offen", text: "Noch kein gültiges Mandat" };
  if (m.qonto_status === "approved") return { klasse: "ls-gut", text: "Bei Qonto bestätigt" };
  if (m.qonto_status === "pending_signature") return { klasse: "ls-offen", text: "Wartet auf Unterschrift" };
  if (m.qonto_status === "canceled") return { klasse: "ls-rot", text: "Bei Qonto storniert" };
  return { klasse: "ls-rot", text: "Bei Qonto nicht angelegt" };
}

function Mandate() {
  const navigate = useNavigate();
  const { data = [], isLoading } = useMandate();
  const { data: vorschau } = useEinzugVorschau();
  const ueberQonto = vorschau?.einzugsweg === "qonto";
  return (
    <>
      <div className="pf-seclabel">Mandate — {data.length} Lastschrift-{data.length === 1 ? "Vertrag" : "Verträge"}</div>
      {isLoading ? <div className="pf-empty">Lade…</div> : data.length === 0 ? (
        <div className="pf-card ls-karte"><p className="ls-text">Noch kein Vertrag mit Lastschrift abgeschlossen.</p></div>
      ) : data.map(m => {
        const z = mandatText(m, ueberQonto);
        return (
          <button key={m.vertrag_id} type="button" className="pf-card ls-karte ls-zeilenknopf"
            onClick={() => navigate(`/vertraege/${m.vertrag_id}`)}>
            <div className="ls-kopf">
              <span className="ls-titel">{m.kunde_name || m.nummer}</span>
              <span className={`ls-marke ${z.klasse}`}>{z.text}</span>
            </div>
            <div className="ls-unterzeile">
              {m.nummer}{m.iban ? ` · ${m.iban}` : ""}
              {m.unterschrieben_am ? ` · unterschrieben ${tag(m.unterschrieben_am)}` : ""}
              {m.erste_faellig && m.qonto_status !== "approved" ? ` · erste Rate ${euro(m.erste_betrag)} am ${tag(m.erste_faellig)}` : ""}
            </div>
            {ueberQonto && m.fehler && !m.qonto_status && <div className="ls-fehler">Qonto: {m.fehler}</div>}
          </button>
        );
      })}
    </>
  );
}

function Laeufe() {
  const { data = [] } = useEinzugLaeufe();
  const erneut = useEinzugsdateiErneut();
  const storno = useLaufStornieren();
  const [stornoFuer, setStornoFuer] = useState<string | null>(null);
  if (data.length === 0) return null;
  return (
    <>
      <div className="pf-seclabel">Letzte Einzüge</div>
      <div className="pf-card ls-karte">
        <ul className="ls-liste">
          {data.map(l => (
            <li key={l.msg_id}>
              <span>{tag(l.faellig_am)} · {l.posten} {l.posten === 1 ? "Rate" : "Raten"} · {euro(l.summe_brutto)}</span>
              <b>
                {[l.bezahlt && `${l.bezahlt} bezahlt`, l.offen && `${l.offen} unterwegs`,
                  l.zurueck && `${l.zurueck} zurück`, l.storniert && `${l.storniert} storniert`]
                  .filter(Boolean).join(" · ")}
              </b>
              {l.weg === "datei" && l.offen > 0 && (
                <span className="ls-knoepfe">
                  <button type="button" className="pf-btn is-ghost ls-knopf" disabled={erneut.isPending}
                    onClick={() => erneut.mutate(l.msg_id)}>Datei erneut laden</button>
                  {stornoFuer === l.msg_id ? (
                    <>
                      <span className="ls-text ls-frage">Nur wenn die Datei nie zur Bank ging — die Raten werden wieder frei.</span>
                      <button type="button" className="pf-btn ls-knopf" disabled={storno.isPending}
                        onClick={() => storno.mutate(l.msg_id, { onSuccess: () => setStornoFuer(null) })}>
                        {storno.isPending ? "Nimmt zurück…" : "Ja, zurücknehmen"}
                      </button>
                      <button type="button" className="pf-btn is-ghost ls-knopf" onClick={() => setStornoFuer(null)}>
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    <button type="button" className="pf-btn is-ghost ls-knopf" onClick={() => setStornoFuer(l.msg_id)}>
                      Nicht zur Bank gegeben
                    </button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
        {erneut.error && <p className="ls-fehler">{fehlertext(erneut.error)}</p>}
        {storno.error && <p className="ls-fehler">{fehlertext(storno.error)}</p>}
      </div>
    </>
  );
}
